import findLastIndex from 'lodash.findlastindex';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { useLoaderData, useSearchParams } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { Spinner } from '@rocco/components/spinner';
import { cn } from '@rocco/utils/cn';
import type { loader as ourProductsLoader } from '@modules/professional/page/loaders/our-products-page-loader.server';
import {
    EMPTY_STATE_SPACING,
    ProfessionalTabEmptyState,
} from './ProfessionalTabEmptyState';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { ProfessionalAppliedFilters } from './ProfessionalAppliedFilters';
import { PROFESSIONAL_TAB_PRODUCT_FILTERS_CONFIG } from '@modules/professional/config/professional-tab-filter-configs';
import { ProfessionalTabFilterSidebar } from './ProfessionalTabFilterSidebar';
import { useFamilyBrandFilter } from '@modules/professional/hook/use-family-brand-filter';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import type { FilterStatesKey } from '~/modules/search/type/filter-state';
import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useProfessionalTabViewMore } from '@modules/professional/hook/use-professional-tab-view-more';
import { useProfessionalHybridLoad } from '@modules/professional/hook/use-professional-hybrid-load';
import {
    useViewportPageSync,
    viewportPageMarkerProps,
} from '~/ui-rocco/hooks/use-viewport-page-sync';
import { useItemAdminData } from '~/modules/directory/hook/use-item-admin-data';
import {
    DIRECTORY_DESKTOP_COL_CLASSES,
    useDirectoryDisplayColumns,
} from '~/ui-rocco/pages/directory-browse/directory-display-columns';
import { DirectoryDisplayDropdown } from '~/ui-rocco/pages/directory-browse/DirectoryDisplayDropdown';
import { LoadMoreButton } from '~/ui-rocco/pages/detail-shared';
import MoreProductsComing from '~/modules/products-category/component/DetailedProductSpliter';
import { ProfessionalProductsFilterBar } from './ProfessionalProductsFilterBar';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import { TAB_CONTENT_GAP } from '../utils/tab-spacing';
import { ProfessionalTabListingBar } from './ProfessionalTabListingBar';
import { useInPageSearchTerm } from '../hooks/use-in-page-search-term';
import {
    TAB_RESULTS_PENDING_CLASS,
    useTabResultsPending,
} from '../utils/use-tab-results-pending';

const IGNORE_FILTER_KEYS: FilterStatesKey[] = [
    ...IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
    'searchLocation',
];

/**
 * Revamped body for the Products tab (/our-products) — the light-theme sibling
 * of {@link import('./ProfessionalProjectsGridSection')}: a big "Products"
 * title + intro, an "(All Products)" divider, the collapsible left filters
 * (starting collapsed behind a toggle, default light theme) and a 4-column
 * editorial grid of product tiles with infinite scroll. Product tiles reuse the
 * shared {@link DetailTileCard} (Save overlay + BM admin menu) with the
 * clear-cut product treatment on a light panel, per the Figma.
 */
export const ProfessionalProductsGridSection = () => {
    const { productTilesRocco, pagination, filterStates, filterStatesRocco } =
        useLoaderData<typeof ourProductsLoader>();
    const { URLSegment, Title } = useProfessionalInitialRouteData();
    const { desktop } = useAppDisplayModeContext();
    const [searchTerm, setSearchTerm] = useInPageSearchTerm();

    // Persisted in the site-wide `showFilter` cookie so the show/hide choice
    // survives navigation and stays consistent with the browse pages.
    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();

    // Keyed separately from the `products` browse grid so a choice made here
    // does not follow the reader out to /products. Default matches that page.
    const { columns, setColumns } = useDirectoryDisplayColumns(
        'professional-products',
        4,
    );

    // The brand dropdown, the sidebar and the sort all navigate; without this
    // the grid sits unchanged for the round trip and a click reads as ignored.
    const resultsPending = useTabResultsPending();

    const {
        tiles: loadedTiles,
        hasMore,
        isLoading,
        loadMore,
        pageStartByIndex,
    } = useProfessionalTabViewMore({
        initialTiles:
            productTilesRocco as TileViewModel<ProductTileViewModel>[],
        pagination,
        urlSegment: URLSegment,
        searchType: 'product',
    });

    // `?page=N` follows the page of tiles actually in view, scrolling either
    // direction — not the pages the infinite-scroll lookahead has fetched.
    useViewportPageSync({
        enabled: hasMore || Object.keys(pageStartByIndex).length > 1,
    });

    const [searchParams] = useSearchParams();
    const { showViewMore, onViewMore, infiniteEnabled, sentinelRef } =
        useProfessionalHybridLoad({
            hasMore,
            isLoading,
            loadMore,
            resetKey: searchParams.toString(),
        });

    const adminDataMap = useItemAdminData(
        loadedTiles.map((tile) => tile.model.id),
    );

    // The browse grid's own tile, rendered from the same view models the loader
    // already hands us. It was previously flattened into a DetailTile and drawn
    // by DetailTileCard — the detail-page editorial card — which is a different
    // component with a different ratio and a 22px type, so the same product
    // read as a different card here and on /products. Stamping the aspect is
    // all the browse grid does on top of the model.
    const tiles: TileViewModel<ProductTileViewModel>[] = loadedTiles.map(
        (tile) => ({
            ...tile,
            aspectRatio: 'productTall' as const,
            // As the legacy Products tab named it, so the tab keeps reporting
            // ProfessionalProductClick rather than the generic tile click.
            section: 'ProfessionalProducts',
            adminData: adminDataMap[tile.model.id],
        }),
    );

    // Desktop column count is the reader's Display choice, matching the browse
    // pages' default of 4 for products. Opening the filter sidebar no longer
    // changes it — CollapsiblePageLayout narrows the content area and the tiles
    // resize into it, which is how the category pages behave.
    const gridColumns = desktop ? columns : 2;

    const productNodes = tiles.map((tile, index) => {
        const card = (
            <CategoryPageTileWithAdminActions key={tile.model.id} tile={tile} />
        );
        const startPage = pageStartByIndex[index];
        return startPage === undefined ? (
            card
        ) : (
            <div key={tile.model.id} {...viewportPageMarkerProps(startPage)}>
                {card}
            </div>
        );
    });

    // When the Buy online (forSale) filter is active, ES ranks purchasable
    // products first and appends the rest. Once the purchasable ones run out,
    // drop in the "no more products for online purchase" divider ahead of the
    // fallback grid, matching the legacy tab.
    if (filterStates.isPurchasable?.checked) {
        const lastPurchasableIndex = findLastIndex(
            loadedTiles,
            (tile) => tile.model.canPurchase,
        );
        const purchasableCount = loadedTiles.filter(
            (tile) => tile.model.canPurchase,
        ).length;
        if (
            purchasableCount === 0 ||
            (lastPurchasableIndex !== -1 &&
                lastPurchasableIndex !== loadedTiles.length - 1)
        ) {
            productNodes.splice(
                lastPurchasableIndex + 1,
                0,
                <MoreProductsComing
                    key="more-products-coming"
                    professionalTitle={Title}
                    columns={gridColumns}
                />,
            );
        }
    }

    // Same selection as the quick-filter bar's chips, as a drawer group.
    const familyBrands = useFamilyBrandFilter();
    const brandFamilyGroup =
        familyBrands.length > 0
            ? {
                  title: 'Brand Family',
                  items: familyBrands.map((brand) => ({
                      id: brand.segment,
                      label: brand.title,
                      checked: brand.checked,
                      to: brand.to,
                  })),
              }
            : undefined;

    const category = useMatchesHandleData<SearchIndex>('category', 'product');
    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys: IGNORE_FILTER_KEYS,
        reportAppliedFiltersAs: 'professionalTab',
    });

    return (
        <section className="w-full overflow-x-clip bg-ground text-[#181818]">
            {/* Desktop keeps the editorial title + intro + CTA cluster; on mobile
                the persistent social-profile header carries the identity/CTAs and
                the tab goes straight to results. */}
            {desktop && (
                <>
                    <div
                        className={cn(
                            'mx-auto w-full max-w-(--ap-page-width) md:px-[3.132rem]',
                            TAB_CONTENT_GAP,
                        )}
                    >
                        {/* The heading repeated the tab pill above it; kept as sr-only
                            so the tab still has a heading in the document outline. */}
                        <h2 className="sr-only">Products</h2>
                    </div>
                </>
            )}
            {/* Quick filters scope the grid below, and the Figma puts them above
                the divider so the "(All Products)" label reads as the result of
                the choice (5526:98663). Rendered on both breakpoints: mobile has
                no other way to reach a sister brand's products. */}
            <div className="mx-auto w-full max-w-(--ap-page-width) max-md:px-6 max-md:pt-3 max-md:empty:hidden md:px-[3.132rem]">
                <ProfessionalProductsFilterBar />
            </div>
            {desktop && (
                <div className="mx-auto w-full max-w-(--ap-page-width) md:px-[3.132rem] md:pt-[2.8rem]">
                    <div className="h-px w-full bg-black/15" />
                </div>
            )}
            {/* Filters + grid. On mobile the tab pill names the page and the
                compact filter bar leads; the persistent header carries identity. */}
            <div className="mx-auto w-full max-w-(--ap-page-width) max-md:px-6 max-md:pt-3 max-md:pb-14 md:px-[3.132rem] md:pt-[1.5rem] md:pb-[7.5rem]">
                <CollapsiblePageLayout
                    paddingClassName="px-0"
                    sidePanel={
                        desktop &&
                        filterStatesRocco && (
                            <div className="h-full md:pt-[1.8rem]">
                                <ProfessionalTabFilterSidebar
                                    searchIndex="product"
                                    config={
                                        PROFESSIONAL_TAB_PRODUCT_FILTERS_CONFIG
                                    }
                                    initialFilterStates={filterStatesRocco}
                                    // The brand family can't come from the
                                    // shared config: it scopes by its own URL
                                    // param, not an ES aggregation.
                                    leadingGroup={brandFamilyGroup}
                                />
                            </div>
                        )
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    {desktop ? (
                        // The listing tabs' bar, as Projects and the rest set
                        // it: the filter toggle with its count on the icon,
                        // search, and Display at the row's end.
                        <ProfessionalTabListingBar
                            search={{
                                value: searchTerm,
                                onChange: setSearchTerm,
                                placeholder: 'Search Products',
                            }}
                            filterDrawer={{
                                open: filterDrawerOpen,
                                onToggle: toggleFilterDrawer,
                            }}
                            filtersCount={
                                searchFilters.appliedFilters?.length ?? 0
                            }
                            display={
                                <DirectoryDisplayDropdown
                                    columns={columns}
                                    onColumnsChange={setColumns}
                                />
                            }
                        />
                    ) : (
                        <SearchFilter
                            searchFilters={searchFilters}
                            filterDrawerOpen={filterDrawerOpen}
                            toggleFilterDrawer={toggleFilterDrawer}
                            searchBarVariant="revamp"
                            mobileSearchPlaceholder="Search Products"
                            hideSort
                            filterControl="plain"
                        />
                    )}
                    {/* The applied-filter bar is desktop-only; on mobile the
                        Refine button's badge + sheet already surface these. */}
                    {desktop &&
                        searchFilters.appliedFilters &&
                        searchFilters.appliedFilters.length > 0 && (
                            <ProfessionalAppliedFilters
                                filterItems={searchFilters.appliedFilters}
                                onClearAll={() =>
                                    searchFilters.resetFilters(
                                        resetFilterStates(
                                            searchFilters.filterStates,
                                            [...IGNORE_FILTER_KEYS, 'category'],
                                        ),
                                    )
                                }
                            />
                        )}

                    {/* Wraps the empty state as well as the grid: a profile whose
                        default scope is its own products can sit at zero (Altus
                        does), and dimming only the grid left that case with no
                        sign the tap registered. The paging controls are inside it
                        too — see the sentinel and View more below. */}
                    <div
                        className={cn(
                            'transition-opacity duration-200',
                            resultsPending && TAB_RESULTS_PENDING_CLASS,
                        )}
                    >
                        {tiles.length === 0 ? (
                            <ProfessionalTabEmptyState
                                resultsType="products"
                                hasFilters
                                className={EMPTY_STATE_SPACING}
                            />
                        ) : (
                            <>
                                <div
                                    className={cn(
                                        'grid grid-cols-2 gap-x-2 gap-y-8 max-md:pt-9 md:gap-x-3 md:gap-y-[3rem] md:pt-[2.4rem]',
                                        DIRECTORY_DESKTOP_COL_CLASSES[columns],
                                    )}
                                >
                                    {productNodes}
                                </div>
                                {infiniteEnabled &&
                                    hasMore &&
                                    !resultsPending && (
                                        <div
                                            ref={sentinelRef}
                                            aria-hidden="true"
                                            style={{ height: 1 }}
                                        />
                                    )}
                                {showViewMore && (
                                    <LoadMoreButton
                                        onClick={onViewMore}
                                        isLoading={resultsPending}
                                        label="View more"
                                        className="pt-[3rem]"
                                    />
                                )}
                                {isLoading && (
                                    <div className="flex justify-center pt-[3rem]">
                                        <Spinner className="size-8 text-black" />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </CollapsiblePageLayout>
            </div>
        </section>
    );
};
